// Shared invisible Cloudflare Turnstile hook. No-op when the site key is not configured.
// Uses a callback ref so widgets render correctly inside conditionally-mounted forms.
import { useCallback, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadScript() {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve();
    let s = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.defer = true;
      document.head.appendChild(s);
    }
    const t = setInterval(() => {
      if (window.turnstile) { clearInterval(t); resolve(); }
    }, 100);
  });
}

export function useTurnstile(action) {
  const widgetId = useRef(null);
  const tokenRef = useRef("");

  const containerRef = useCallback((node) => {
    if (!SITE_KEY) return;
    if (!node) {
      if (widgetId.current != null && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
        tokenRef.current = "";
      }
      return;
    }
    loadScript().then(() => {
      if (widgetId.current != null || !node.isConnected) return;
      widgetId.current = window.turnstile.render(node, {
        sitekey: SITE_KEY,
        action,
        appearance: "interaction-only",
        callback: (t) => { tokenRef.current = t; },
        "expired-callback": () => { tokenRef.current = ""; },
        "error-callback": () => { tokenRef.current = ""; },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getToken = () => tokenRef.current;
  const reset = () => {
    tokenRef.current = "";
    if (widgetId.current != null && window.turnstile) window.turnstile.reset(widgetId.current);
  };
  return { containerRef, getToken, reset };
}
